import { useEffect, useMemo, useRef, useState } from 'react';

import { EditOutlined, PlusOutlined, TagOutlined } from '@ant-design/icons';
import { App, Button, Tag, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { LeadTag } from 'models/Lead';

import { LeadService } from 'services/LeadService';

import { diffTags } from 'utils/LeadTagEditorHelper';

import appI18n from '../../../config/i18n';
import LeadTagPicker from './LeadTagPicker';

const I18N = 'scenes.leads.components.detail_lead_modal';

interface LeadTagsEditorProps {
  leadId: string;
  /** Country the tag catalog is scoped to (the lead's own country). */
  country: string;
  tags: LeadTag[];
  /** `savedLeadId` lets the parent ignore a save that resolved late. */
  onSaved: (tags: LeadTag[], savedLeadId: string) => void;
  /** Reports whether the editor has unsaved changes. */
  onDirtyChange?: (dirty: boolean) => void;
}

function LeadTagsEditor({
  leadId,
  country,
  tags,
  onSaved,
  onDirtyChange,
}: LeadTagsEditorProps) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const { token } = theme.useToken();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;

  const initialNames = useMemo(() => tags.map((tag) => tag.name), [tags]);
  const { added, removed } = diffTags(initialNames, draft);
  const dirty = editing && (added.length > 0 || removed.length > 0);

  useEffect(() => {
    onDirtyRef.current?.(dirty);
  }, [dirty]);

  // Clear the flag if the editor unmounts while dirty (lead navigation).
  useEffect(() => () => onDirtyRef.current?.(false), []);

  const startEditing = () => {
    setDraft(initialNames);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft([]);
  };

  const handleSave = async () => {
    setSaving(true);
    const response = await LeadService.updateLeadTags(leadId, draft);
    setSaving(false);
    if (response.success) {
      onSaved((response.data as LeadTag[]) ?? [], leadId);
      message.success(t(`${I18N}.tags.save_success`));
      setEditing(false);
      setDraft([]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${I18N}.tags.save_failed`),
        3.5
      );
    }
  };

  const labelNode = (
    <span
      style={{
        fontSize: 12,
        color: token.colorTextTertiary,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <TagOutlined />
      {t(`${I18N}.tags.label`)}
    </span>
  );

  const linkButton = (icon: React.ReactNode, text: string) => (
    <Button
      type="link"
      size="small"
      icon={icon}
      onClick={startEditing}
      style={{ padding: 0, height: 'auto' }}
    >
      {text}
    </Button>
  );

  if (!editing) {
    return (
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        {labelNode}
        {tags.map((tag) => (
          <Tag key={tag.id} style={{ margin: 0 }}>
            {tag.name}
          </Tag>
        ))}
        {tags.length > 0
          ? linkButton(<EditOutlined />, t(`${I18N}.buttons.edit`))
          : linkButton(<PlusOutlined />, t(`${I18N}.tags.add`))}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ marginBottom: 4 }}>{labelNode}</div>
      <LeadTagPicker
        country={country}
        value={draft}
        onChange={setDraft}
        isHighlighted={(name) => added.includes(name)}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginTop: 8,
        }}
      >
        <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
          {dirty ? (
            <>
              {added.length > 0 && (
                <span style={{ color: token.colorSuccess }}>
                  {t(`${I18N}.tags.summary_added`, { count: added.length })}
                </span>
              )}
              {removed.length > 0 && (
                <span
                  style={{
                    color: token.colorError,
                    marginLeft: added.length > 0 ? 8 : 0,
                  }}
                >
                  {t(`${I18N}.tags.summary_removed`, {
                    count: removed.length,
                    names: removed.join(', '),
                  })}
                </span>
              )}
            </>
          ) : (
            t(`${I18N}.tags.no_changes`)
          )}
        </span>
        <Button size="small" onClick={cancelEditing} disabled={saving}>
          {t(`${I18N}.buttons.cancel`)}
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!dirty}
          loading={saving}
          onClick={handleSave}
        >
          {t(`${I18N}.buttons.save_changes`)}
        </Button>
      </div>
    </div>
  );
}

export default LeadTagsEditor;
